import React, { useEffect, useRef, useState } from 'react';
import { 
  FaQuoteLeft, FaStar, FaArrowRight, FaRegHeart, FaShareAlt
} from 'react-icons/fa';
import { MdFormatQuote, MdVerified } from 'react-icons/md';

const TestimonialsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "DR. VIDYASAGAR UPADHYAY",
      role: "Principal",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      quote: "R.D.S.Snatkottar Mahavidyalaya has been my second home for over 15 years. The dedication of our students makes this institution truly special.",
      rating: 5
    },
    {
      id: 2,
      name: "Priya Sharma",
      role: "BA Student",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      quote: "The faculty here is exceptional! They don't just teach; they mentor. The library and computer labs are top-notch.",
      rating: 5
    },
    {
      id: 3,
      name: "Amit Patel",
      role: "Software Engineer",
      image: "https://randomuser.me/api/portraits/men/45.jpg",
      quote: "My journey from this college to Google wouldn't have been possible without the strong foundation I built here.",
      rating: 5
    },
    {
      id: 4,
      name: "Dr. Meena Gupta",
      role: "Science Professor",
      image: "https://randomuser.me/api/portraits/women/68.jpg",
      quote: "Watching our students grow into confident professionals is the most rewarding part of my job.",
      rating: 5
    },
    {
      id: 5,
      name: "Rahul Verma",
      role: "IIM Ahmedabad",
      image: "https://randomuser.me/api/portraits/men/52.jpg",
      quote: "The values and discipline I learned here shaped my character. This college is truly a launchpad for success.",
      rating: 5
    }
  ];

  return (
    <section ref={sectionRef} className="py-16 px-6 md:px-10 bg-gradient-to-b from-blue-50 to-pink-50/30">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 bg-pink-100 px-4 py-1.5 rounded-full mb-3">
            <MdFormatQuote className="text-blue-400 text-sm" />
            <span className="text-blue-600 font-semibold text-xs">Student Voices</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
            What Our Community Says
          </h2>
          <div className="w-16 h-0.5 bg-gradient-to-r from-pink-400 to-blue-600 mx-auto rounded-full mt-3"></div>
        </div>

        {/* 5 Cards Row */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {testimonials.map((item, idx) => (
            <div 
              key={idx}
              className="group bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              {/* Profile */}
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-blue-200"
                  />
                  <div className="absolute -bottom-0.5 -right-0.5 bg-green-500 rounded-full p-0.5">
                    <MdVerified className="text-white text-[8px]" />
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">{item.name}</h3>
                  <p className="text-xs text-blue-400">{item.role}</p>
                </div>
              </div>

              {/* Quote */}
              <FaQuoteLeft className="text-blue-200 text-lg mb-2" />
              <p className="text-gray-600 text-xs leading-relaxed mb-3 line-clamp-3">
                "{item.quote}"
              </p>

              {/* Rating */}
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className={`text-[10px] ${i < item.rating ? 'text-yellow-400' : 'text-gray-200'}`} />
                ))}
              </div>

              {/* Button */}
              <button className="w-full py-1.5 text-xs font-medium text-blue-600 bg-pink-50 rounded-lg group-hover:bg-blue-400 group-hover:text-white transition-all duration-300">
                Read More
              </button>
            </div>
          ))}
        </div>

        {/* Share Button */}
        <div className={`text-center mt-8 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <button className="group px-6 py-2 bg-gradient-to-r from-blue-400 to-blue-700 text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto">
            <FaRegHeart className="text-xs" />
            Share Your Story
            <FaArrowRight className="group-hover:translate-x-1 transition-transform text-xs" />
          </button>
        </div>
      </div>

      <style jsx>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default TestimonialsSection;